/**
 * Input bridge - Sends raw input states to server
 * Server calculates physics, client no longer predicts
 */

import { TICK_HZ } from "@game/config";
import type { InputProvider } from "@game/domain";
import type { RawInput } from "@game/protocol";
import {
  getActiveRealtimeBackend,
  isActiveRealtimeBackendConnected,
} from "../networking/realtime.runtime";

// Input frame for transmission tracking
export interface InputFrame {
  sequence: number;
  input: RawInput;
  timestamp: number;
}

// Input sequencing
let inputSequence = 0;
let inputBuffer: InputFrame[] = [];
let inputTransmissionTimer: ReturnType<typeof setInterval> | null = null;
let activeProvider: InputProvider | null = null;

type RealtimeInputDependencies = {
  getLobbyId: () => string | null;
};

let realtimeInputDependencies: RealtimeInputDependencies = {
  getLobbyId: () => null,
};

export function setRealtimeInputDependencies(
  dependencies: Partial<RealtimeInputDependencies>,
): void {
  realtimeInputDependencies = {
    ...realtimeInputDependencies,
    ...dependencies,
  };
}

/**
 * Set the active input provider
 */
export function setInputProvider(provider: InputProvider): void {
  activeProvider = provider;
}

/**
 * Get input sequence number
 */
export function getInputSequence(): number {
  return inputSequence;
}

/**
 * Get input buffer for client prediction
 */
export function getInputBuffer(): InputFrame[] {
  return [...inputBuffer];
}

/**
 * Clear input buffer (for game reset/reconnection)
 */
export function clearInputBuffer(): void {
  inputBuffer = [];
}

/**
 * Remove acknowledged inputs from buffer
 */
export function removeAcknowledgedInputs(acknowledgedSequence: number): void {
  inputBuffer = inputBuffer.filter(
    (frame) => frame.sequence > acknowledgedSequence,
  );
}

/**
 * Set/clear locked thrust angle for strafe mode
 * NOTE: Server now handles thrust angle locking via shift key edge detection
 * This function is kept for backward compatibility but is a no-op
 */
export function setLockedThrustAngle(_angle: number | undefined): void {
  // No-op: Server handles thrust angle locking
}

/**
 * Process and transmit raw input to server
 */
function processInputTransmission(): void {
  if (!activeProvider) return;

  // Get raw input state from provider
  const rawInputState = activeProvider.getInputState();

  // Process boost input (detects keypress vs hold)
  // Convert to protocol RawInput format (server calculates offsets and edges)
  const input: RawInput = {
    keyForward: rawInputState.keyForward,
    keyBack: rawInputState.keyBack,
    keyLeft: rawInputState.keyLeft,
    keyRight: rawInputState.keyRight,
    keyBoost: rawInputState.keyBoost || pendingBoostTrigger, // Include UI button trigger
    keyShift: rawInputState.keyShift,
    mouseScreenX: rawInputState.mouseScreenX,
    mouseScreenY: rawInputState.mouseScreenY,
    canvasWidth: rawInputState.canvasWidth,
    canvasHeight: rawInputState.canvasHeight,
    mouseShoot: rawInputState.mouseShoot,
  };

  // Increment sequence
  inputSequence++;

  // Create input frame for tracking
  const inputFrame: InputFrame = {
    sequence: inputSequence,
    input,
    timestamp: performance.now(),
  };

  // Add to buffer for tracking
  inputBuffer.push(inputFrame);

  // Limit buffer size
  if (inputBuffer.length > 120) {
    inputBuffer.shift();
  }

  const realtimeBackend = getActiveRealtimeBackend();
  const realtimeBackendConnected = isActiveRealtimeBackendConnected();
  const activeLobbyId = realtimeInputDependencies.getLobbyId();

  if (realtimeBackend && realtimeBackendConnected && activeLobbyId) {
    void realtimeBackend
      .send({
        type: "submit_input",
        lobbyId: activeLobbyId,
        sequence: inputSequence,
        input,
      })
      .catch((error) => {
        console.error("Failed to send realtime input command", error);
      });
    return;
  }
}

/**
 * Start input transmission to server at server tick rate
 */
export function startInputTransmission(): void {
  if (inputTransmissionTimer) return; // Already running

  const intervalMs = Math.floor(1000 / TICK_HZ); // Match server tick rate
  inputTransmissionTimer = setInterval(processInputTransmission, intervalMs);
}

/**
 * Stop input transmission to server
 */
export function stopInputTransmission(): void {
  if (inputTransmissionTimer) {
    clearInterval(inputTransmissionTimer);
    inputTransmissionTimer = null;
  }
}

/**
 * Update input provider (no-op for raw input, kept for compatibility)
 */
export function updateInput(deltaTime: number): void {
  if (activeProvider) {
    activeProvider.update(deltaTime);
  }
}

/**
 * Trigger boost ability (for UI buttons)
 * Simulates spacebar press for one transmission cycle
 */
let pendingBoostTrigger = false;
let boostTriggerTimeout: ReturnType<typeof setTimeout> | null = null;

export function triggerBoost(): void {
  pendingBoostTrigger = true;

  // Clear previous timeout if it exists to prevent race conditions
  if (boostTriggerTimeout) {
    clearTimeout(boostTriggerTimeout);
  }

  // Clear after 50ms to ensure at least one transmission
  boostTriggerTimeout = setTimeout(() => {
    pendingBoostTrigger = false;
    boostTriggerTimeout = null;
  }, 50);
}

/**
 * Get current boost trigger state
 */
export function getBoostTrigger(): boolean {
  return pendingBoostTrigger;
}
